import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { api } from "../../lib/api";
import AssignmentCard from "../../components/crm/AssignmentCard";
import TasksCard from "../../components/crm/TasksCard";
import ActivityTimeline from "../../components/crm/ActivityTimeline";
import SiteVisitScheduler from "../../components/crm/SiteVisitScheduler";
import SalesDocsPanel from "../../components/sales/SalesDocsPanel";
import { errMsg } from "../../components/crm/crmUtil";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

function Linked({ title, rows, render, testId }) {
  return (
    <section className="border border-erf-borderCream rounded bg-white p-4" data-testid={testId}>
      <h3 className="font-display font-semibold mb-2">{title} ({rows.length})</h3>
      {rows.length === 0 ? <p className="text-xs font-mono text-erf-dark/60">None yet.</p> : <ul className="space-y-1 text-sm">{rows.map(render)}</ul>}
    </section>
  );
}

// B5: opportunity detail — summary, linked account / contacts / lead, sales documents, assignment, tasks, activity, visits.
export default function OpportunityDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get(`/opportunities/${id}`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))), [id]);
  useEffect(() => { load(); }, [load]);
  const o = d?.opportunity;
  return (
    <AppShell title={o?.name || "Opportunity"} subtitle={o ? `${o.stage} · ${money(o.expected_value)} · ${Math.round((o.probability || 0) * 100)}%` : ""}>
      <div className="mx-auto w-full max-w-[1100px] px-4 md:px-6 space-y-4" data-testid="opportunity-detail">
        {err && <p role="alert" className="text-sm text-red-700" data-testid="opp-error">{err}</p>}
        {!d && !err && <p className="font-mono text-xs" data-testid="opp-loading">Loading…</p>}
        {d && <>
          <div className="grid gap-4 md:grid-cols-3">
            <Linked title="Account" testId="opp-account" rows={d.account ? [d.account] : []} render={(a) => <li key={a.id}><Link to={`/crm/accounts/${a.id}`} className="underline inline-flex min-h-[44px] items-center" data-testid="opp-account-link">{a.name || a.display_name || a.id.slice(0, 8)}</Link></li>} />
            <Linked title="Contacts" testId="opp-contacts" rows={d.contacts} render={(c) => <li key={c.id}><Link to={`/crm/contacts/${c.id}`} className="underline inline-flex min-h-[44px] items-center" data-testid={`opp-contact-link-${c.id}`}>{[c.first_name, c.last_name].filter(Boolean).join(" ")} {c.email ? `· ${c.email}` : ""}</Link></li>} />
            <Linked title="Lead" testId="opp-lead" rows={d.lead ? [d.lead] : []} render={(l) => <li key={l.id}><Link to={`/crm/leads/${l.id}`} className="underline inline-flex min-h-[44px] items-center" data-testid="opp-lead-link">{l.first_name} {l.last_name} · {l.status}</Link></li>} />
          </div>
          <AssignmentCard entity="opportunity" id={id} record={o} onSaved={load} />
          <div className="grid gap-4 md:grid-cols-3">
            <Linked title="Estimates" testId="opp-estimates" rows={d.estimates} render={(e) => <li key={e.id}>{e.name} · {money(e.total)} · {e.status}</li>} />
            <Linked title="Proposals" testId="opp-proposals" rows={d.proposals} render={(p) => <li key={p.id}>{p.status} · {(p.created_at || "").slice(0, 10)}</li>} />
            <Linked title="Contracts" testId="opp-contracts" rows={d.contracts} render={(c) => <li key={c.id}>{c.status} · {(c.created_at || "").slice(0, 10)}</li>} />
          </div>
          <SalesDocsPanel oppId={id} estimates={d.estimates} proposals={d.proposals} contracts={d.contracts} onChange={load} />
          <section className="border border-erf-borderCream rounded bg-white p-4" aria-labelledby="opp-visits-title">
            <h3 id="opp-visits-title" className="font-display font-semibold mb-2">Site visits & inspections</h3>
            <SiteVisitScheduler leadId={d.lead?.id} opportunityId={id} visits={d.site_visits} onChange={load} />
          </section>
          <div className="grid gap-4 lg:grid-cols-2">
            <TasksCard entity="opportunity" id={id} />
            <ActivityTimeline entity="opportunity" id={id} />
          </div>
        </>}
      </div>
    </AppShell>
  );
}
